import type { FC, MouseEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import icons from '@icons';
import { Picture } from '@ui';
import { Link, useLocation } from '@compat/router';
import { observer } from 'mobx-react';
import { useStores } from '@root/src/stores';
import { preparePriceFloatObject } from '@utils/floatNumbers';
import { OPEN_MOBILE_CATALOG_EVENT, CLOSE_MOBILE_CATALOG_EVENT, MOBILE_CATALOG_VISIBILITY_EVENT, MOBILE_SEARCH_VISIBILITY_EVENT, OPEN_MOBILE_SEARCH_EVENT, CLOSE_MOBILE_SEARCH_EVENT } from '@constants/customEvents';
import { twcls } from '@utils/twcls';
import './index.scss';

type BottomTab =
  | 'home'
  | 'catalog'
  | 'search'
  | 'favorites'
  | 'cart'
  | 'profile';

const {
  homeMobile: { active: homeActiveOutlined, filled: homeFilledOutlined },
  gridMobile: { active: gridActiveOutlined, filled: gridFilledOutlined },
  searchMobile: { active: searchActiveOutlined, filled: searchFilledOutlined },
  heartMobile: { active: heartActiveOutlined, filled: heartFilledOutlined },
  shoppingCartMobile: {
    active: shoppingCartActiveOutlined,
    filled: shoppingCartFilledOutlined
  },
  personMobile: {
    active: personActiveOutlined,
    filled: personFilledOutlined
  }
} = icons.Outline;

const MobileBottomNav: FC = observer(() => {
  const { pathname } = useLocation();
  const { modalsStore, userStore, cartStore } = useStores();
  const cartTotal = preparePriceFloatObject(cartStore.cart?.total || 0);
  const isCartPage = pathname === '/cart' || pathname === '/cart/';
  const isHomePage = pathname === '/' || pathname === '';
  const isFavoritesPage = pathname.startsWith('/cabinet/products');
  const isProfilePage = pathname.startsWith('/cabinet') && !isFavoritesPage;
  const routeTab: BottomTab | null = isHomePage
    ? 'home'
    : isFavoritesPage
      ? 'favorites'
      : isCartPage
        ? 'cart'
        : isProfilePage
          ? 'profile'
          : null;
  const hasUnreadNotifications =
    userStore.isUserLoggedIn &&
    Number(userStore.notCheckedNotificationsAmount) > 0;
  const [isHiddenOnScroll, setIsHiddenOnScroll] = useState(false);
  const [isCatalogOpen, setIsCatalogOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<BottomTab | null>(routeTab);
  const pendingTabRef = useRef<BottomTab | null>(null);
  const lastScrollYRef = useRef(0);

  const selectTab = (tab: BottomTab): void => {
    pendingTabRef.current = tab;
    setActiveTab(tab);
    if (tab !== 'search') {
      setIsSearchOpen(false);
      window.dispatchEvent(new Event(CLOSE_MOBILE_SEARCH_EVENT));
    }
    if (tab !== 'catalog') {
      setIsCatalogOpen(false);
      window.dispatchEvent(new Event(CLOSE_MOBILE_CATALOG_EVENT));
    }
  };

  const focusSearch = (): void => {
    selectTab('search');
    setIsSearchOpen(true);
    window.dispatchEvent(new Event(OPEN_MOBILE_SEARCH_EVENT));
  };

  const openCatalog = (event: MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    selectTab('catalog');
    setIsCatalogOpen(true);
    window.dispatchEvent(new Event(OPEN_MOBILE_CATALOG_EVENT));
  };

  const openProfile = (): void => {
    if (!userStore.isUserLoggedIn) {
      modalsStore.changeAuthModalState();
    }
  };

  const openFavorites = (event: MouseEvent<HTMLAnchorElement>): void => {
    if (!userStore.isUserLoggedIn) {
      event.preventDefault();
      modalsStore.changeAuthModalState();
      return;
    }

    window?.dataLayer?.push({ event: 'webit_click_favorites' });
  };

  useEffect(() => {
    const onCatalogVisibility = (event: Event): void => {
      const customEvent = event as CustomEvent<boolean>;
      const isOpen = Boolean(customEvent.detail);
      if (
        pendingTabRef.current &&
        pendingTabRef.current !== 'catalog'
      ) {
        return;
      }
      setIsCatalogOpen(isOpen);
      if (isOpen && !pendingTabRef.current) {
        setActiveTab('catalog');
      }
    };

    window.addEventListener(
      MOBILE_CATALOG_VISIBILITY_EVENT,
      onCatalogVisibility
    );
    return () => {
      window.removeEventListener(
        MOBILE_CATALOG_VISIBILITY_EVENT,
        onCatalogVisibility
      );
    };
  }, []);

  useEffect(() => {
    const onSearchVisibility = (event: Event): void => {
      const customEvent = event as CustomEvent<boolean>;
      const isOpen = Boolean(customEvent.detail);
      if (pendingTabRef.current && pendingTabRef.current !== 'search') {
        return;
      }
      setIsSearchOpen(isOpen);
      if (isOpen && !pendingTabRef.current) {
        setActiveTab('search');
      }
    };

    window.addEventListener(MOBILE_SEARCH_VISIBILITY_EVENT, onSearchVisibility);
    return () => {
      window.removeEventListener(
        MOBILE_SEARCH_VISIBILITY_EVENT,
        onSearchVisibility
      );
    };
  }, []);

  useEffect(() => {
    if (!isCartPage) {
      setIsHiddenOnScroll(false);
      document.body.classList.remove('mobile-bottom-nav-hidden');
      return;
    }

    lastScrollYRef.current = window.scrollY;

    const handleScroll = (): void => {
      const currentScrollY = window.scrollY;
      const lastScrollY = lastScrollYRef.current;
      const isScrollingDown =
        currentScrollY > lastScrollY && currentScrollY > 8;

      setIsHiddenOnScroll(prev =>
        isScrollingDown ? true : currentScrollY < lastScrollY ? false : prev
      );
      lastScrollYRef.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.body.classList.remove('mobile-bottom-nav-hidden');
    };
  }, [isCartPage]);

  useEffect(() => {
    document.body.classList.toggle(
      'mobile-bottom-nav-hidden',
      isCartPage && isHiddenOnScroll
    );
  }, [isCartPage, isHiddenOnScroll]);

  useEffect(() => {
    const pending = pendingTabRef.current;
    if (pending === 'home' && isHomePage) {
      pendingTabRef.current = null;
    } else if (pending === 'favorites' && isFavoritesPage) {
      pendingTabRef.current = null;
    } else if (pending === 'cart' && isCartPage) {
      pendingTabRef.current = null;
    } else if (pending === 'profile' && isProfilePage) {
      pendingTabRef.current = null;
    } else if (pending === 'search' && isSearchOpen) {
      pendingTabRef.current = null;
    } else if (pending === 'catalog' && isCatalogOpen) {
      pendingTabRef.current = null;
    } else if (pending) {
      return;
    }

    if (isSearchOpen) {
      setActiveTab('search');
      return;
    }
    if (isCatalogOpen) {
      setActiveTab('catalog');
      return;
    }
    setActiveTab(routeTab);
  }, [
    isHomePage,
    isFavoritesPage,
    isCartPage,
    isProfilePage,
    isSearchOpen,
    isCatalogOpen,
    routeTab
  ]);

  const personIcon =
    activeTab === 'profile' ? personActiveOutlined : personFilledOutlined;
  const notificationsCount = Number(userStore.notCheckedNotificationsAmount);

  return (
    <nav
      className={twcls(
        'mobile-bottom-nav',
        isHiddenOnScroll && 'mobile-bottom-nav--hidden'
      )}
    >
      <Link
        className='mobile-bottom-nav__item'
        to='/'
        aria-label='Главная'
        onPointerDown={() => {
          selectTab('home');
        }}
      >
        <Picture
          icon={activeTab === 'home' ? homeActiveOutlined : homeFilledOutlined}
          width='24'
          height='24'
          alt=''
        />
      </Link>
      <button
        className='mobile-bottom-nav__item'
        type='button'
        aria-label='Каталог'
        onMouseDown={event => {
          event.stopPropagation();
        }}
        onClick={openCatalog}
      >
        <Picture
          icon={activeTab === 'catalog' ? gridActiveOutlined : gridFilledOutlined}
          width='24'
          height='24'
          alt=''
        />
      </button>
      <button
        className='mobile-bottom-nav__item'
        type='button'
        aria-label='Поиск'
        onPointerDown={event => {
          event.preventDefault();
          focusSearch();
        }}
      >
        <Picture
          icon={activeTab === 'search' ? searchActiveOutlined : searchFilledOutlined}
          width='24'
          height='24'
          alt=''
        />
      </button>
      <Link
        className='mobile-bottom-nav__item'
        to='/cabinet/products'
        aria-label='Избранное'
        onClick={openFavorites}
        onPointerDown={() => {
          if (userStore.isUserLoggedIn) {
            selectTab('favorites');
          }
        }}
      >
        <Picture
          icon={
            activeTab === 'favorites' ? heartActiveOutlined : heartFilledOutlined
          }
          width='24'
          height='24'
          alt=''
        />
      </Link>
      <Link
        className='mobile-bottom-nav__item mobile-bottom-nav__item--cart'
        to='/cart'
        aria-label='Корзина'
        onPointerDown={() => {
          selectTab('cart');
        }}
      >
        <Picture
          icon={
            activeTab === 'cart'
              ? shoppingCartActiveOutlined
              : shoppingCartFilledOutlined
          }
          width='24'
          height='24'
          alt=''
        />
        {Number(cartStore.cart?.total) > 0 && (
          <span className='mobile-bottom-nav__cart-total'>
            {cartTotal.wholePart} ₽
          </span>
        )}
      </Link>
      {userStore.isUserLoggedIn ? (
        <Link
          className='mobile-bottom-nav__item mobile-bottom-nav__item--profile'
          to='/cabinet'
          aria-label='Личный кабинет'
          onPointerDown={() => {
            selectTab('profile');
          }}
        >
          <Picture
            icon={personIcon}
            width='24'
            height='24'
            alt=''
          />
          {hasUnreadNotifications && (
            <span
              className={twcls(
                'mobile-bottom-nav__notifications',
                notificationsCount > 9 &&
                  'mobile-bottom-nav__notifications--wide'
              )}
            >
              {notificationsCount}
            </span>
          )}
        </Link>
      ) : (
        <button
          className='mobile-bottom-nav__item'
          type='button'
          aria-label='Войти'
          onClick={openProfile}
        >
          <Picture
            icon={personIcon}
            width='24'
            height='24'
            alt=''
          />
        </button>
      )}
    </nav>
  );
});

export default MobileBottomNav;
